import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { isSupabaseError } from "../../../../../constants/supabaseErrors";
import type { DocumentType, IncidentDatabase } from "../../../../../types";
import emptyHtmlToNull from "../../../../../utils/emptyHtmlToNull";
import { useChangeDocumentsOfResource } from "../../../../documents/hooks/useChangeDocumentsOfResource";
import { useGetDocumentsByEntity } from "../../../api/useGetDocumentsByEntity";
import { useUpdateIncident } from "../../../api/useUpdateIncident";
import type { Document } from "../../actionModales/types";

const documentsSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  file_url: z.string().nullable(),
  document_type_id: z.string().nullable().optional(),
  type: z.custom<DocumentType>().nullable().optional(),
  pendingFile: z.custom<File>().optional(),
});

export const editIncidentFormSchema = z.object({
  title: z.string().trim().min(1, 'Ce champ est obligatoire'),
  date: z.date({ error: 'Ce champ est obligatoire' }),
  status: z.enum(['in_progress', 'resolved']),
  provider_name: z.string().trim().optional(),
  contact_name: z.string().trim().optional(),
  comment: z.string().trim().optional().transform(emptyHtmlToNull),
  documents: z.array(documentsSchema),
});

export type EditIncidentFormValues = z.infer<typeof editIncidentFormSchema>;

type Props = {
  incident: IncidentDatabase;
  subjectId: string;
  onClose: () => void;
};

export function useEditIncidentModal(props: Props) {
  const [serverError, setServerError] = useState<string | null>(null);
  const [showDocuments, setShowDocuments] = useState(false);

  const { data: originalDocuments, isLoading: isDocumentLoading } = useGetDocumentsByEntity(
    { incidentId: props.incident.id },
    true
  );

  const { mutateAsync: updateIncident, isPending: isUpdating } = useUpdateIncident();
  const { uploadPendingFiles, saveDocumentChanges, isPending: isDocsUpdating } =
    useChangeDocumentsOfResource();

  const isResolved = props.incident.status === 'resolved';


  const form = useForm({
    mode: "onChange",
    defaultValues: {
      title: props.incident.title ?? '',
      date: props.incident.date ? new Date(props.incident.date) : new Date(),
      status: (isResolved ? 'resolved' : 'in_progress') as 'in_progress' | 'resolved',
      provider_name: props.incident.provider_name ?? '',
      contact_name: props.incident.contact_name ?? '',
      comment: props.incident.comment ?? '',
      documents: [] as Document[],
    },
    resolver: zodResolver(editIncidentFormSchema),
  });

  useEffect(() => {
    if (isDocumentLoading || !originalDocuments) {
      return;
    }

    const docs: Document[] = originalDocuments.map((doc) => ({
      id: doc.id,
      file_url: doc.file_url,
      name: doc.name,
      document_type_id: doc.document_type_id,
      type: doc.type,
      provider_id: doc.provider_id,
    }));

    form.setValue("documents", docs, { shouldDirty: false });
    if (docs.length > 0) {
      setShowDocuments(true);
    }
  }, [originalDocuments, isDocumentLoading, form]);

  async function handleConfirm(data: EditIncidentFormValues) {
    setServerError(null);

    try {
      const uploadedDocs = await uploadPendingFiles(data.documents, {
        subjectId: props.subjectId,
      });

      await updateIncident({
        id: props.incident.id,
        subject_id: props.subjectId,
        title: data.title,
        date: data.date.toISOString(),
        status: data.status,
        comment: data.comment,
        provider_name: data.provider_name || null,
        contact_name: data.contact_name || null,
      });

      await saveDocumentChanges(
        originalDocuments ?? [],
        uploadedDocs,
        {
          incident_id: props.incident.id,
          subject_action_id: props.incident.id,
          sub_topic_id: props.subjectId,
        }
      );

      props.onClose();
    } catch (error: unknown) {
      if (isSupabaseError(error)) {
        setServerError(error.message);
        return;
      }

      setServerError("Une erreur est survenue lors de la modification de l'incident.");
    }
  }

  const documents = form.watch("documents");

  return {
    form,
    handleConfirm,
    isLoading: isUpdating || isDocsUpdating,
    serverError,
    isDocumentLoading,
    showDocuments: showDocuments || documents.length > 0,
    revealDocuments: () => setShowDocuments(true),
  };
}
